import { cn } from '@cobliteam/colib-ui/cn';
import type { Transition, Variants } from 'motion/react';
import { m } from 'motion/react';
import type { HTMLAttributes } from 'react';
import { forwardRef } from 'react';

export interface GiftIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface GiftIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
  isHovered: boolean;
}

const defaultTransition: Transition = {
  duration: 1.8,
  ease: 'easeInOut',
};

const boxVariants: Variants = {
  normal: {
    scale: 1,
  },
  animate: {
    scale: [0.8, 1.05, 1],
    transition: {
      ...defaultTransition,
      delay: 0.1,
      duration: 0.4,
    },
  },
};

const ribbonVariants: Variants = {
  normal: {
    pathLength: 1,
    opacity: 1,
  },
  animate: {
    pathLength: [0, 1],
    opacity: [0, 1],
    transition: {
      ...defaultTransition,
      delay: 0.3,
      duration: 0.3,
    },
  },
};

const bowVariants: Variants = {
  normal: {
    pathLength: 1,
    opacity: 1,
    scale: 1,
  },
  animate: {
    pathLength: [0, 1],
    opacity: [0, 1],
    scale: [0, 1.2, 1],
    transition: {
      ...defaultTransition,
      delay: 0.6,
      duration: 0.4,
    },
  },
};

const GiftIcon = forwardRef<GiftIconHandle, GiftIconProps>(({ onMouseEnter, onMouseLeave, className, size = 28, isHovered, ...props }, ref) => {
  const variant = isHovered ? 'animate' : 'normal';

  return (
    <div className={cn('relative overflow-hidden', className)} style={{ width: size, height: size }} {...props}>
      <svg
        data-testid='lucide-gift'
        xmlns='http://www.w3.org/2000/svg'
        width={18}
        height={18}
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
        className='lucide lucide-gift-icon lucide-gift'
      >
        <m.g variants={boxVariants} animate={variant}>
          <rect x='3' y='8' width='18' height='4' rx='1' />
          <path d='M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7' />
        </m.g>

        <m.path d='M12 8v13' variants={ribbonVariants} animate={variant} />

        <m.path
          d='M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5'
          variants={bowVariants}
          animate={variant}
          style={{ transformOrigin: '12px 6px' }}
        />
      </svg>
    </div>
  );
});

GiftIcon.displayName = 'GiftIcon';

export { GiftIcon };
